import { EventEmitterInterface } from '../event/emitter.interface';
import { urlBuildQueryParams } from '../../helper/url/build-query-params';
import { urlSerializeParams } from '../../helper/url/serialize-params';
import { UrlQueryParamsInterface } from '../../helper/url/query-params.interface';
import { WindowServiceInterface } from '../../service/window/service.interface';
import { WindowLocationServiceInterface } from '../../service/window-location/service.interface';
import { BrowserLocationParamsPayloadInterface } from './location/params-payload.interface';
import { BrowserLocationUpdateMethodEnum } from './location/update-method.enum';
import { BrowserLocationEvent } from './location.event';
import { BrowserWindowEvent } from './window.event';

/**
 * Track the browser's window location and dynamically control and sync it
 */
export class BrowserLocation implements WindowLocationServiceInterface {
  /**
   *
   * @param windowService
   * @param eventEmitter
   */
  constructor(private windowService: WindowServiceInterface, private eventEmitter: EventEmitterInterface) {
    this.initialize();
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritDoc
   */
  public getHref(): string {
    return this.windowService.getNative().location.href;
  }

  /**
   * @inheritDoc
   */
  public getHash(): string {
    return this.windowService.getNative().location.hash.slice(1);
  }

  /**
   * @inheritDoc
   */
  public getPathname(): string {
    return this.windowService.getNative().location.pathname;
  }

  /**
   * @inheritDoc
   */
  public getHostname(): string {
    return this.windowService.getNative().location.hostname;
  }

  /**
   * @inheritDoc
   */
  public getQueryData(): UrlQueryParamsInterface {
    return urlBuildQueryParams(this.windowService.getNative().location.search);
  }

  /**
   * @inheritDoc
   */
  public redirect(pathname: string, payload: BrowserLocationParamsPayloadInterface): void {
    this.go(pathname, payload);
  }

  /**
   * @inheritDoc
   */
  public push(pathname: string, payload: BrowserLocationParamsPayloadInterface): void {
    this.go(pathname, payload, BrowserLocationUpdateMethodEnum.push);
  }

  /**
   * @inheritDoc
   */
  public replace(pathname: string, payload: BrowserLocationParamsPayloadInterface): void {
    this.go(pathname, payload, BrowserLocationUpdateMethodEnum.replace);
  }

  /**
   * Set the window location or history based on inputted values
   *
   * @param pathname
   * @param payload
   * @param method
   */
  private go(
    pathname: string,
    payload: BrowserLocationParamsPayloadInterface,
    method?: BrowserLocationUpdateMethodEnum
  ): void {
    const window = this.windowService.getNative();

    const href = this.toHref(pathname, payload.hash, payload.queryData);

    switch (method) {
      case BrowserLocationUpdateMethodEnum.push:
        window.history.pushState({}, window.document.title, href);
        break;

      case BrowserLocationUpdateMethodEnum.replace:
        window.history.replaceState({}, window.document.title, href);
        break;

      case BrowserLocationUpdateMethodEnum.redirect:
      default:
        window.location.href = href;
    }

    this.eventEmitter.emit(BrowserLocationEvent.update, {
      hash: payload.hash,
      pathname: pathname,
      queryData: payload.queryData,
    });
  }

  /**
   * Attach listeners
   */
  private observers(): void {
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.popstate, this.onPopstateWindow);
  }

  /**
   * Initialize the location watcher
   */
  private initialize(): void {
    this.eventEmitter.emit(BrowserLocationEvent.update, {
      hash: this.getHash(),
      pathname: this.getPathname(),
      queryData: this.getQueryData(),
    });
  }

  /**
   * Return a full href-like string concatenating location properties
   *
   * TODO-FE[later] Update this method to support more flexible url parsing
   * (absolute path, href vs pathname, parsing hash and data from href)
   *
   * @param pathname
   * @param hash
   * @param queryData
   */
  private toHref(pathname: string, hash: string, queryData: UrlQueryParamsInterface): string {
    const hrefArr =
      pathname.indexOf(this.windowService.getNative().location.origin) === 0
        ? []
        : [this.windowService.getNative().location.origin];

    if (pathname) {
      hrefArr.push(pathname);
    }

    if (Object.keys(queryData).length) {
      hrefArr.push('?' + urlSerializeParams(queryData));
    }

    if (hash) {
      hrefArr.push('#' + hash);
    }

    return hrefArr.join('');
  }

  /**
   * Update when the history object has been modified from the window
   */
  private onPopstateWindow = (): void => {
    const locationData = {
      hash: this.getHash(),
      pathname: this.getPathname(),
      queryData: this.getQueryData(),
    };

    this.eventEmitter.emit(BrowserLocationEvent.update, locationData);
    this.eventEmitter.emit(BrowserLocationEvent.pop, locationData);
  };
}
